import { memo, useMemo, useRef } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
/** Redux */
import { setSingleDayLabelAction } from '../../../redux/slices/analyses';
import { useDispatch, useSelector } from 'react-redux';
import { extractModalStateFromAnalysis } from '../../../redux/selectors/analyses';
/** Events handler */
import useIncidents from '../hooks/useIncidents';
/** CHART COMPONENTS */
import CustomYAxisTick from '../CustomYAxisTick';
import CustomTooltip from '../CustomTooltip';
/** INTERFACES */
/** UTILS */
import { isRightOrCmdMetaClickMouseDownEvent } from '../utils';
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  CHART_MARGIN,
  DATA_KEY,
  DEFAULT_X_AXIS_CONFIG,
  getXAxisPadding,
} from '../constants/utils';
import { useYAxisBounds } from '../hooks';
import { isNil } from 'lodash';
import {
  useAxesProps,
  useChartLines,
  useFormattedChartData,
  usePrepareCohort,
  useSingleChartDataTooltip,
  useYAxisWidth,
} from '../hooks';
import { assertCondition } from '../../../utils/types';
import {
  ExpressionModeKey,
  QueryType,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { bootstrapTimezoneSelector } from '../../../redux/selectors/bootstrap';
import { getMeasureFormatByColumnKey } from '../CustomTooltip/utils';
import { generateCustomLabels } from './generateCustomLabels';
import { useAppSelector } from '../../../redux/hooks';
import { YAxisPosition } from '../interfaces';
import ComposedAxisLabel from '../ComposedAxisLabel';
import { ChartContextMenu, useChartContextMenu } from '../ChartContextMenu';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useBuilderInfoDisplay from '../utils/useBuilderInfoDisplay';
import { useOptimizedChartData } from '../../../utils/hooks/useOptimizedChartData';
import { expandedMenuDataSchema } from '../ChartContextMenu/useChartContextMenu';
import { ChartMode } from '../types';
import calculateBarSize from '../utils/calculateBarSize';
import SingleChartDataTooltip from '../SingleChartDataTooltip';
import { useTooltipWheelContext } from '../CustomTooltip/CustomTooltipWheelContext';
import StyledBar from './StyledBar';
import { useBarChartOpacity } from '../hooks/useBarChartOpacity';
import useCacheParsedChartLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheParsedChartLlmRawDataEffect';
import { useFloatingLayerContext } from '../../FloatingManager';
import { freeze } from '@reduxjs/toolkit';
import { IContextualMenuItem } from '@fluentui/react';
import ContextualMenuOption from '../../Dropdown/ContextualMenu/ContextualMenuOption';
import PopperPortal from '../CustomTooltip/PopperPortal';
import { IBreakdownOverallStatus } from '../../../utils/interfaces';
import CustomXAxisTick from '../CustomXAxisTick';
import { useChartContext } from '../ChartContext';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

export default memo(function BarChart({ analysisId, mode }: IProps) {
  const { tooltipContentRef } = useTooltipWheelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const modalsStateMemoizedPropSelector = useMemo(
    extractModalStateFromAnalysis,
    [],
  );
  /* Chart modals state */
  const isCreateIncidentModalOpen = useSelector((state) =>
    modalsStateMemoizedPropSelector(
      state,
      analysisId,
      'isCreateIncidentModalOpen',
    ),
  );
  const createIncidentData = useSelector((state) =>
    modalsStateMemoizedPropSelector(state, analysisId, 'createIncidentData'),
  );
  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);
  const {
    chartData,
    displayedSlices,
    settings: { chartSettings, chartWidth, incidents },
    metadata: { isBreakdownOverall, timeUnit, query },
    displayedMeasures,
    colorConfig,
  } = useChartContext();

  const { chartType, activeIncidentTypes, axisSettings } = chartSettings;
  // narrow down chartData to IParsedChartDataExpression
  assertCondition(chartData.queryType === QueryType.Expression);
  const { linesMeta, data, dataKeysOnMeasure } = chartData;

  const chartDates = useMemo(
    () => data?.map((singleDataPoint: any) => singleDataPoint.date as string),
    [data],
  );

  const { visibleLines } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const {
    onBarMouseMove,
    onMouseLeave,
    tooltipProps,
    clearLockedTooltipProps,
    lockTooltipPositionHandler,
    isTooltipLocked,
  } = useSingleChartDataTooltip({
    containerRef,
  });

  // KBT-1311, disable rechart tooltip when no breakdown in Bar Chart
  const enableRechartTooltip =
    isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdown;
  const { getBarOpacity, setActiveLabel } = useBarChartOpacity();

  const { handleCreateCohortFromExpression } = usePrepareCohort({
    analysisId,
  });
  const { menuTarget, menuData, openChartContextMenu, closeChartContextMenu } =
    useChartContextMenu();

  const allowToExpand = mode !== ChartMode.Compact;

  const handleExpand = () => {
    const result = expandedMenuDataSchema.safeParse(menuData);
    if (result.success) {
      dispatch(
        setSingleDayLabelAction({
          analysisId,
          singleDayLabel: result.data.activeLabel,
        }),
      );
      closeChartContextMenu();
    }
  };

  const contextualMenuItems = freeze<IContextualMenuItem[]>(
    [
      {
        key: 'expand-bar',
        onRenderContent: () => (
          <ContextualMenuOption label={'Expand'} onClick={handleExpand} />
        ),
      },
    ],
    true,
  );

  const singleDayTrigger = (rechartEvent: any, mouseDownEvent: any) => {
    // if not right click, or not CMD + left click or Ctrl + left click, return.
    if (!isRightOrCmdMetaClickMouseDownEvent(mouseDownEvent)) {
      return;
    }
    assertCondition(query.__typename === 'ExpressionQuery');
    // KBT-1654: if Histogram Mode + All Time, we don't display timeSeries on X-axis by default.
    // we would like to disable the expand feature to keep the consistency
    if (
      query.expressionMode.key === ExpressionModeKey.Histogram &&
      timeUnit === TimeUnit.AllTime
    ) {
      return;
    }

    openChartContextMenu(mouseDownEvent, rechartEvent);
  };

  const { getYAxisDomain } = useYAxisBounds();

  const { leftAxisProps, rightAxisProps, getMeasureYAxisPositionByKey } =
    useAxesProps();

  const hasLeftAxis = !isNil(leftAxisProps);
  const hasRightAxis = !isNil(rightAxisProps);
  const isDisplayMeasureLabel = displayedMeasures.length > 1;
  const yDefaultAxisId = hasLeftAxis
    ? YAxisPosition.LEFT
    : hasRightAxis
      ? YAxisPosition.RIGHT
      : undefined;

  const { renderIncidents, renderCreateIncidentReferenceLine } = useIncidents({
    analysisId,
    incidents,
    createIncidentData,
    isCreateIncidentModalOpen,
    chartDates,
    activeIncidentTypes,
    yAxisId: yDefaultAxisId,
  });

  const formattedChartData = useFormattedChartData({
    chartData: data,
    displayedMeasures,
    dataKeysOnMeasure,
  });
  const { xAxisSeries, yAxisDataKeyCreator, xAxisKey } = useOptimizedChartData(
    formattedChartData,
    DATA_KEY,
  );

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const barSize = calculateBarSize(chartData.dates.length, chartWidth);
  const tooltipZIndex = useFloatingLayerContext();

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheParsedChartLlmRawDataEffect({
    chartTypeName: 'Bar',
    parsedChartDataRecords: formattedChartData,
    analysisId,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth({
    label:
      leftAxisProps && isDisplayMeasureLabel ? leftAxisProps.measureName : '',
  });

  return (
    <>
      <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
        <ResponsiveContainer ref={containerRef}>
          <RechartsBarChart
            data={xAxisSeries}
            margin={CHART_MARGIN}
            barCategoryGap={'12%'}
            onMouseDown={allowToExpand ? singleDayTrigger : undefined}
            onMouseMove={(props) => {
              setActiveLabel(props.activeLabel ?? null);
            }}
            onMouseLeave={() => {
              setActiveLabel(null);
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={xAxisKey}
              {...DEFAULT_X_AXIS_CONFIG}
              tick={({ ...props }) => (
                <CustomXAxisTick
                  {...props}
                  limit={barSize}
                  shouldRotate={false}
                />
              )}
              padding={getXAxisPadding({ hasLeftAxis, hasRightAxis })}
            />
            {hasLeftAxis && (
              <YAxis
                padding={{ top: 16, bottom: 0 }}
                yAxisId={YAxisPosition.LEFT}
                orientation="left"
                hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
                tickSize={0}
                domain={getYAxisDomain({
                  yAxisPosition: YAxisPosition.LEFT,
                  visibleLines,
                })}
                allowDataOverflow={true}
                axisLine={false} // Whether to display the Y axis vertical line or not
                tick={
                  <CustomYAxisTick
                    measureDisplayFormat={leftAxisProps.format}
                  />
                }
                tickCount={5}
                tickMargin={10}
                width={yAxisWidth}
              >
                {isDisplayMeasureLabel && (
                  <Label
                    value={leftAxisProps.measureName}
                    position="left"
                    fontWeight={500}
                    angle={-90}
                    content={(props) => (
                      <ComposedAxisLabel
                        props={props}
                        chartWidth={chartWidth}
                        displayedMeasures={displayedMeasures.filter(
                          (measure) =>
                            measure.yAxisPosition === YAxisPosition.LEFT &&
                            measure.isVisible,
                        )}
                      />
                    )}
                  />
                )}
              </YAxis>
            )}
            {hasRightAxis && (
              <YAxis
                padding={{ top: 16, bottom: 0 }}
                yAxisId={YAxisPosition.RIGHT}
                orientation="right"
                hide={
                  !axisSettings.yAxisSettings[YAxisPosition.RIGHT].isVisible
                }
                tickSize={0}
                domain={getYAxisDomain({
                  yAxisPosition: YAxisPosition.RIGHT,
                  visibleLines,
                })}
                allowDataOverflow={true}
                axisLine={false} // Whether to display the Y axis vertical line or not
                tick={
                  <CustomYAxisTick
                    measureDisplayFormat={rightAxisProps.format}
                  />
                }
                tickCount={5}
                tickMargin={10}
                // need to handle the case when there is no left axis
                width={hasLeftAxis ? yAxisWidth : yAxisWidth + 32}
              >
                {isDisplayMeasureLabel && (
                  <Label
                    value={rightAxisProps.measureName}
                    position="right"
                    fontWeight={500}
                    angle={90}
                    content={(props) => (
                      <ComposedAxisLabel
                        props={props}
                        chartWidth={chartWidth}
                        displayedMeasures={displayedMeasures.filter(
                          (measure) =>
                            measure.yAxisPosition === YAxisPosition.RIGHT &&
                            measure.isVisible,
                        )}
                      />
                    )}
                  />
                )}
              </YAxis>
            )}
            <Tooltip
              cursor={false}
              offset={20}
              wrapperStyle={{
                zIndex: tooltipZIndex,
                position: mode === ChartMode.Compact ? 'fixed' : 'absolute',
              }}
              allowEscapeViewBox={{ x: false, y: false }}
              content={(props: TooltipProps<number, string>) => {
                return isNil(tooltipProps) && enableRechartTooltip ? (
                  <PopperPortal active={props.active}>
                    {CustomTooltip({
                      props,
                      lines: visibleLines,
                      chartType,
                      chartValues: formattedChartData,
                      linesMeta,
                      timezone: orgTimezone,
                      displayedMeasures,
                      displayedSlices,
                      additionalLabel: builderInfoDisplay,
                      colorConfig,
                      isBreakdownOverall,
                      tooltipPayloadContentRef: tooltipContentRef,
                    })}
                  </PopperPortal>
                ) : null;
              }}
            />
            {renderIncidents}
            {visibleLines.map((line, index) => {
              const yAxisId = getMeasureYAxisPositionByKey(line.measureKey);
              return (
                <Bar
                  key={`bar-${index}`}
                  isAnimationActive={false}
                  name={line.name}
                  dataKey={yAxisDataKeyCreator(line.name)}
                  strokeDasharray={'1 0'}
                  strokeWidth={BAR_CHART_STROKE_WIDTH}
                  minPointSize={BAR_CHART_MIN_POINT_SIZE}
                  barSize={barSize}
                  yAxisId={yAxisId}
                  onMouseMove={(eventProps, _, mouseElement) => {
                    onBarMouseMove({
                      rechartProps: { ...eventProps, xAxisKey },
                      line,
                      mouseEvent: mouseElement,
                    });
                  }}
                  onMouseLeave={onMouseLeave}
                  onClick={lockTooltipPositionHandler}
                  fill={line.color}
                  stroke={line.type}
                  shape={<StyledBar />}
                >
                  <LabelList
                    dataKey={line.name}
                    content={(props) =>
                      generateCustomLabels(
                        formattedChartData,
                        props,
                        line,
                        getMeasureFormatByColumnKey({
                          columnKey: line.name,
                          linesMeta,
                          displayedMeasures,
                        }),
                      )
                    }
                  />
                  {data.map((entry, index) => {
                    const opacity = getBarOpacity({
                      tooltipProps,
                      xAxisValue: entry[xAxisKey] as string,
                      lineName: line.name,
                      isTooltipLocked,
                      disableRechartTooltip: !enableRechartTooltip,
                    });
                    return <Cell key={`cell-${index}`} opacity={opacity} />;
                  })}
                </Bar>
              );
            })}
            {renderCreateIncidentReferenceLine}
          </RechartsBarChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromExpression}
          tooltipProps={tooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
        />
      )}
      {menuTarget && (
        <ChartContextMenu
          items={contextualMenuItems}
          menuTarget={menuTarget}
          onClose={closeChartContextMenu}
        />
      )}
    </>
  );
});
